import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ImageOff, Images } from "lucide-react";
import { SiteLayout, PageBanner } from "../components/site/SiteLayout";
import { api, fileUrl } from "../lib/api";
import { usePrefs } from "../context/PrefsContext";

export default function Galeri() {
  const [cats, setCats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState(null);
  const { t } = usePrefs();

  useEffect(() => {
    api.get("/gallery/categories").then(({ data }) => setCats(data)).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setItems(null);
    api.get(`/gallery?category_id=${selected.id}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [selected]);

  return (
    <SiteLayout>
      {(content) => (
        <div data-testid="galeri-page">
          <PageBanner title={selected ? selected.name : t.nav.gallery} subtitle={selected ? null : t.banner.gallerySub} image={content.page_banners?.gallery?.image} video={content.page_banners?.gallery?.video} posY={content.page_banners?.gallery?.pos_y} />
          <section className="py-24 bg-slate-50 dark:bg-slate-950 min-h-[40vh]">
            <div className="max-w-7xl mx-auto px-6">
              {selected && (
                <button
                  onClick={() => { setSelected(null); setItems(null); }}
                  data-testid="gallery-back-btn"
                  className="mb-10 text-orange-600 dark:text-orange-500 font-semibold text-sm hover:underline"
                >
                  {t.gallery.back}
                </button>
              )}

              {!selected ? (
                cats === null ? (
                  <p className="text-slate-500 dark:text-slate-400">{t.gallery.loading}</p>
                ) : cats.length === 0 ? (
                  <div className="text-center py-20" data-testid="gallery-empty">
                    <ImageOff className="text-slate-400 dark:text-slate-700 mx-auto mb-4" size={44} />
                    <p className="text-slate-500 dark:text-slate-400">{t.gallery.empty}</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-category-grid">
                    {cats.map((c, i) => (
                      <motion.button
                        key={c.id}
                        onClick={() => setSelected(c)}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                        className="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm dark:shadow-none text-left"
                        data-testid={`gallery-category-${c.id}`}
                      >
                        {c.cover ? (
                          <img
                            src={fileUrl(c.cover)}
                            alt={c.name}
                            loading="lazy"
                            className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full aspect-[4/3] flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                            <Images className="text-slate-400 dark:text-slate-600" size={40} />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 via-slate-950/60 to-transparent p-4 pt-10">
                          <p className="text-white text-sm font-bold uppercase tracking-wide">{c.name}</p>
                          <p className="text-slate-300 text-xs mt-0.5">{c.count} {t.gallery.photos}</p>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )
              ) : items === null ? (
                <p className="text-slate-500 dark:text-slate-400">{t.gallery.loading}</p>
              ) : items.length === 0 ? (
                <div className="text-center py-20" data-testid="gallery-cat-empty">
                  <ImageOff className="text-slate-400 dark:text-slate-700 mx-auto mb-4" size={44} />
                  <p className="text-slate-500 dark:text-slate-400">{t.gallery.emptyCat}</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
                  {items.map((item, i) => (
                    <motion.figure
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                      className="group relative overflow-hidden rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm dark:shadow-none"
                      data-testid={`gallery-item-${item.id}`}
                    >
                      <img
                        src={fileUrl(item.url)}
                        alt={item.caption || selected.name}
                        loading="lazy"
                        className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {item.caption && (
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950/90 to-transparent p-4 text-white text-sm font-medium">
                          {item.caption}
                        </figcaption>
                      )}
                    </motion.figure>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </SiteLayout>
  );
}
